import { Form, Spin, Input, Select, Checkbox } from "antd";
import { Button as PrimeBtn } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import React, { useEffect, useRef, useState } from "react";

import { requestUpdateProjectCapacity } from "../../../apis/SettingApis";
import { daysOfWeek, SET_CURRENT_USER } from "../../../utils/constants";
import { dispatch } from "../../../store";
import { useSelector } from "react-redux";

const RequestProjectCapacityDialog = (props) => {
    const [apiLoading, setApiLoading] = useState(false);
    const { currentUser = {} } = useSelector(({ app }) => app);
    const toast = useRef(null);
    const [form] = Form.useForm();
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [requestedDayOffs, setRequestedDayOffs] = useState([]);

    // Add a date range to the list
    const handleAddPeriod = () => {
        if (!startDate || !endDate) {
            alert("Please select both start and end dates.");
            return;
        }
        if (new Date(startDate) > new Date(endDate)) {
            alert("Start date must be before or equal to end date.");
            return;
        }
        const newPeriod = `${startDate},${endDate}`;
        var results = ([...requestedDayOffs, newPeriod]);
        setRequestedDayOffs(results);

        form.setFieldsValue({
            requestedDayOffs: results.join(";"),
            startDate: "",
            endDate: ""
        });

        setStartDate("");
        setEndDate("");
    };

    useEffect(() => {
        form.setFieldsValue({
            startDate: "",
            endDate: "",
            requestedDayOffs: props.user?.requestedDayOffs,
            ...daysOfWeek.reduce((acc, day) => {       
                return {
                    ...acc,
                    [`req${day}ContractorCapacity`]: props.user?.capacityDto?.[`req${day}ContractorCapacity`],
                    [`req${day}QcerCapacity`]: props.user?.capacityDto?.[`req${day}QcerCapacity`],
                    [`req${day}FinalCheckerCapacity`]: props.user?.capacityDto?.[`req${day}FinalCheckerCapacity`],
                };
            }, {}),
        });
        setStartDate("");
        setEndDate("");
        setRequestedDayOffs(props.user?.requestedDayOffs != "" ? props.user?.requestedDayOffs?.split(";"): []);
    }, [props]);

    const handleSubmit = () => {
        form.validateFields().then((values) => {
            values.id = props.user?.id;

            let roles = [
                "ContractorCapacity",
                "QcerCapacity",
                "FinalCheckerCapacity"
            ];

            let capacityDto = {};

            daysOfWeek.forEach(day => {
                let lowerDay = day.toLowerCase();
                roles.forEach(role => {
                    // original capacity
                    const key = `${lowerDay}${role}`;
                    capacityDto[key] = props.user?.capacityDto?.[key];

                    // request capacity
                    const reqKey = `req${day}${role}`;
                    capacityDto[reqKey] = values?.[reqKey];
                });
            });

            values.capacityDto = capacityDto;

            setApiLoading(true);
            requestUpdateProjectCapacity(values).then((res) => {
                if (res.status === 200) {
                    toast.current.show({
                        severity: "success",
                        summary: "Status",
                        detail: "Update User successfully",
                        life: 3000
                    });
                    props.setVisible(false);
                    
                    if (props.user.requestType !== "REQUESTED") {
                        dispatch({
                            type: SET_CURRENT_USER,
                            payload: {
                                ...currentUser,
                                numChangeRequest: currentUser.numChangeRequest + 1,
                            },
                        });
                    }
                } else {
                    toast.current.show({
                        severity: "warn",
                        summary: "Status",
                        detail: "Update User failed",
                        life: 3000
                    });
                    props.setVisible(false);
                }
                setApiLoading(false);
            }).catch((e) => {
                toast.current.show({
                    severity: "error",
                    summary: "Status",
                    detail: e,
                    life: 3000,
                });
                setApiLoading(false);
            });
        });
    };

    return (
        <React.Fragment>
            <Toast ref={toast} />
            <Dialog
                style={{ width: "660px" }}
                header={"Request update Project Capacity: " + props.user?.username}
                visible={props.visible}
                onHide={() => { props.setVisible(false); }}
            >
                <Spin spinning={apiLoading}>
                    <div className='flex justify-content-between align-items-center'>
                        <Form form={form} onFinish={handleSubmit}>
                            {/* Dynamic capacities for each day */}
                            {daysOfWeek.map((day) => {
                                return (
                                    <div key={day}>
                                        <h4>{day}</h4>
                                        <div style={{ display: "flex", gap: "20px" }}>
                                            <Form.Item
                                                hidden={!props.showContractor}
                                                required={true}
                                                name={`req${day}ContractorCapacity`}
                                                label={`Contractor`}
                                                labelCol={{ span: 12 }}
                                                labelAlign="left"
                                            >
                                                <Input
                                                    style={{ width: "100px" }}
                                                    onKeyDown={(e) => e.stopPropagation()}
                                                />
                                            </Form.Item>

                                            <Form.Item
                                                hidden={!props.showQCer}
                                                required={true}
                                                name={`req${day}QcerCapacity`}
                                                label={`QCer`}
                                                labelCol={{ span: 12 }}
                                                labelAlign="right"
                                            >
                                                <Input
                                                    style={{ width: "100px" }}
                                                    onKeyDown={(e) => e.stopPropagation()}
                                                />
                                            </Form.Item>

                                            <Form.Item
                                                hidden={!props.showFinalChecker}
                                                required={true}
                                                name={`req${day}FinalCheckerCapacity`}
                                                label={`Final Checker`}
                                                labelCol={{ span: 14 }}
                                                labelAlign="right"
                                            >
                                                <Input
                                                    style={{ width: "100px" }}
                                                    onKeyDown={(e) => e.stopPropagation()}
                                                />
                                            </Form.Item>

                                        </div>
                                    </div>
                                );
                            })}
                            <Form.Item
                                label="Start Date:"
                                name="startDate"
                                rules={[{ required: false, message: "Please select the start date!" }]}
                            >
                                <Input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                />
                            </Form.Item>

                            <Form.Item
                                label="End Date:"
                                name="endDate"
                                rules={[{ required: false, message: "Please select the end date!" }]}
                            >
                                <Input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                />
                            </Form.Item>

                            <Form.Item>
                                <PrimeBtn
                                    style={{
                                        backgroundColor: "#037eea",
                                        borderColor: "#037eea",
                                        float: "right",
                                        marginRight: "1rem",
                                    }}
                                    onClick={handleAddPeriod}
                                    label="Add Request Period Off"
                                    type="button"
                                />
                            </Form.Item>

                            <Form.Item
                                label="Unavailable Periods Request:"
                                name="requestedDayOffs"
                            >
                                <Input
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>

                            <div style={{ display: "flex", justifyContent: "center" }}>
                                <PrimeBtn
                                    style={{
                                        backgroundColor: "#037eea",
                                        borderColor: "#037eea",
                                        float: "right",
                                        marginRight: "1rem",
                                    }}
                                    type="submit"
                                    label="Submit"
                                />
                            </div>
                        </Form>
                    </div>
                </Spin>
            </Dialog>
        </React.Fragment>
    );
};

export default RequestProjectCapacityDialog;
